import { gql } from "urql";

export const CREATE_ORGANISATION = gql`
  mutation CreateOrganisation($input: CreateOrganisationInput!) {
    createOrganisation(input: $input) {
      organisation {
        id
        name
        slug
      }
      errors
    }
  }
`;

export const CREATE_CLIENT = gql`
  mutation CreateClient($input: CreateClientInput!) {
    createClient(input: $input) {
      client {
        id
        name
        industry
        website
      }
      errors
    }
  }
`;

export const UPDATE_CLIENT = gql`
  mutation UpdateClient($input: UpdateClientInput!) {
    updateClient(input: $input) {
      client {
        id
        name
        industry
        website
        branding
        preferences
        logoUrl
        searchCountryCodes
        searchLocations {
          name
          countryCode
        }
      }
      errors
    }
  }
`;

export const DELETE_CLIENT = gql`
  mutation DeleteClient($input: DeleteClientInput!) {
    deleteClient(input: $input) {
      impact {
        campaigns
        exclusiveCandidates
        sharedCandidates
        talentPools
        clientContacts
        notes
        portalUsers
      }
      enqueued
      errors
    }
  }
`;

export const CREATE_CAMPAIGN = gql`
  mutation CreateCampaign(
    $title: String!
    $clientId: ID!
    $briefNotes: String
    $hiringManagerNotes: String
    $otherNotes: String
  ) {
    createCampaign(
      input: {
        title: $title
        clientId: $clientId
        briefNotes: $briefNotes
        hiringManagerNotes: $hiringManagerNotes
        otherNotes: $otherNotes
      }
    ) {
      campaign {
        id
        title
        status
        currentStage
        client {
          id
          name
        }
      }
      errors
    }
  }
`;

export const UPDATE_CAMPAIGN = gql`
  mutation UpdateCampaign($input: UpdateCampaignInput!) {
    updateCampaign(input: $input) {
      campaign {
        id
        title
        status
        currentStage
        client {
          id
          name
        }
      }
      errors
    }
  }
`;

export const ARCHIVE_CAMPAIGN = gql`
  mutation ArchiveCampaign($input: ArchiveCampaignInput!) {
    archiveCampaign(input: $input) {
      campaign {
        id
        archivedAt
        archiveReason
      }
      errors
    }
  }
`;

export const UNARCHIVE_CAMPAIGN = gql`
  mutation UnarchiveCampaign($input: UnarchiveCampaignInput!) {
    unarchiveCampaign(input: $input) {
      campaign {
        id
        archivedAt
      }
      errors
    }
  }
`;

export const ADVANCE_CAMPAIGN_STAGE = gql`
  mutation AdvanceCampaignStage($input: AdvanceCampaignStageInput!) {
    advanceCampaignStage(input: $input) {
      campaign {
        id
        currentStage
      }
      stageChange {
        id
        fromStage
        toStage
        reason
        createdAt
      }
      errors
    }
  }
`;

export const CREATE_CANDIDATE = gql`
  mutation CreateCandidate($input: CreateCandidateInput!) {
    createCandidate(input: $input) {
      candidate {
        id
        firstName
        lastName
        fullName
        email
        currentRoleTitle
        currentCompany
      }
      campaignCandidate {
        id
        stage
        candidate { id fullName }
      }
      talentPoolMembership {
        id
        candidate { id fullName }
      }
      errors
    }
  }
`;

export const UPDATE_CANDIDATE = gql`
  mutation UpdateCandidate($input: UpdateCandidateInput!) {
    updateCandidate(input: $input) {
      candidate {
        id
        firstName
        lastName
        fullName
        email
        phone
        linkedinUrl
        currentRoleTitle
        currentCompany
        location
        notes
        consentStatus
        consentGivenAt
        consentNotes
        resumes {
          id
          filename
          url
        }
      }
      errors
    }
  }
`;

export const ATTACH_CANDIDATE_RESUME = gql`
  mutation AttachCandidateResume($input: AttachCandidateResumeInput!) {
    attachCandidateResume(input: $input) {
      candidate {
        id
        resumes {
          id
          filename
          url
        }
      }
      errors
    }
  }
`;

export const DETACH_CANDIDATE_RESUME = gql`
  mutation DetachCandidateResume($input: DetachCandidateResumeInput!) {
    detachCandidateResume(input: $input) {
      candidate {
        id
        resumes {
          id
          filename
          url
        }
      }
      errors
    }
  }
`;

export const IMPORT_LINKEDIN_PROFILE = gql`
  mutation ImportLinkedinProfile($input: ImportLinkedinProfileInput!) {
    importLinkedinProfile(input: $input) {
      backgroundTask {
        id
        kind
        title
        state
        subjectType
        subjectId
        parentTaskId
        errorMessage
        result
        startedAt
        finishedAt
        updatedAt
      }
      candidate {
        id
        fullName
        currentRoleTitle
        currentCompany
      }
      errors
    }
  }
`;

export const IMPORT_LINKEDIN_TO_TALENT_POOL = gql`
  mutation ImportLinkedinToTalentPool($input: ImportLinkedinToTalentPoolInput!) {
    importLinkedinToTalentPool(input: $input) {
      backgroundTask {
        id
        kind
        title
        state
        subjectType
        subjectId
        parentTaskId
        errorMessage
        result
        startedAt
        finishedAt
        updatedAt
      }
      talentPoolMembership {
        id
        candidate { id fullName }
      }
      errors
    }
  }
`;

export const IMPORT_LINKEDIN_TO_CAMPAIGN = gql`
  mutation ImportLinkedinToCampaign($input: ImportLinkedinToCampaignInput!) {
    importLinkedinToCampaign(input: $input) {
      backgroundTask {
        id
        kind
        title
        state
        subjectType
        subjectId
        parentTaskId
        errorMessage
        result
        startedAt
        finishedAt
        updatedAt
      }
      campaignCandidate {
        id
        stage
        candidate {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const ADD_CANDIDATE_TO_CAMPAIGN = gql`
  mutation AddCandidateToCampaign($input: AddCandidateToCampaignInput!) {
    addCandidateToCampaign(input: $input) {
      campaignCandidate {
        id
        stage
        candidate {
          id
          fullName
        }
      }
      talentPoolMembership {
        id
        candidate {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const ADD_CANDIDATE_TO_CAMPAIGN_POOL = gql`
  mutation AddCandidateToCampaignPool($input: AddCandidateToCampaignPoolInput!) {
    addCandidateToCampaignPool(input: $input) {
      talentPoolMembership {
        id
        candidate {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const PROMOTE_POOL_MEMBERSHIP = gql`
  mutation PromotePoolMembership($input: PromotePoolMembershipInput!) {
    promotePoolMembership(input: $input) {
      campaignCandidate {
        id
        stage
        candidate {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const RETURN_CANDIDATE_TO_POOL = gql`
  mutation ReturnCandidateToPool($input: ReturnCandidateToPoolInput!) {
    returnCandidateToPool(input: $input) {
      talentPoolMembership {
        id
        returnedFromStage
        returnedReason
        returnedAt
        candidate {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const UPDATE_CAMPAIGN_BRIEF = gql`
  mutation UpdateCampaignBrief($input: UpdateCampaignBriefInput!) {
    updateCampaignBrief(input: $input) {
      campaignBrief {
        id
        currentVersion {
          id
          additionalNotes
          versionNumber
          author {
            id
            fullName
          }
          effectiveFrom
          changeSummary
          salaryBase
          salaryBonus
          salaryEquity
          salaryBenefits
          salaryCurrency
          targetStartDate
          assessmentProcess
          backgroundCheckRequirements
          roleTitle
          roleMandate
          contextNotes
          successOutcomes
          culturalFitNotes
          motivationFitNotes
          rawBriefNotes
          searchCountryCodes
          searchLocations {
            name
            countryCode
          }
        }
      }
      errors
    }
  }
`;

export const TOGGLE_CAMPAIGN_BRIEF_LOCK = gql`
  mutation ToggleCampaignBriefLock($input: ToggleCampaignBriefLockInput!) {
    toggleCampaignBriefLock(input: $input) {
      campaignBrief {
        id
        locked
        lockedAt
        lockedBy {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const UPDATE_STAKEHOLDERS = gql`
  mutation UpdateStakeholders($input: UpdateStakeholdersInput!) {
    updateStakeholders(input: $input) {
      campaignBrief {
        id
        stakeholders {
          id
          name
          stakeholderRole
          position
          userId
          user {
            id
            fullName
          }
        }
      }
      errors
    }
  }
`;

export const UPDATE_SELECTION_STEPS = gql`
  mutation UpdateSelectionSteps($input: UpdateSelectionStepsInput!) {
    updateSelectionSteps(input: $input) {
      campaignBrief {
        id
        selectionProcess {
          id
          description
          steps {
            id
            continuityId
            stepType
            label
            position
            changeReason
            stepFormat
            durationMinutes
            participants {
              role
              name
              organisationMembershipId
            }
            purpose
            origin
            inFlightCandidateCount
          }
        }
      }
      errors
    }
  }
`;

export const UPDATE_SELECTION_PROCESS = gql`
  mutation UpdateSelectionProcess($input: UpdateSelectionProcessInput!) {
    updateSelectionProcess(input: $input) {
      campaignBrief {
        id
        selectionProcess {
          id
          description
          currentVersion {
            id
            versionNumber
            origin
          }
        }
      }
      errors
    }
  }
`;

export const UPDATE_STEP_ASSESSMENT_PLAN = gql`
  mutation UpdateStepAssessmentPlan($input: UpdateStepAssessmentPlanInput!) {
    updateStepAssessmentPlan(input: $input) {
      stepAssessmentPlan {
        id
        selectionStepContinuityId
        intent
        coverage {
          dimensionId
          depth
          dimensionName
          dimensionInCurrentMatrix
        }
        currentVersion {
          id
          versionNumber
          origin
          acknowledgedAt
        }
      }
      errors
    }
  }
`;

export const RESTORE_SELECTION_STEP = gql`
  mutation RestoreSelectionStep($input: RestoreSelectionStepInput!) {
    restoreSelectionStep(input: $input) {
      campaignBrief {
        id
        selectionProcess {
          id
          description
          steps {
            id
            continuityId
            stepType
            label
            position
            changeReason
            stepFormat
            durationMinutes
            participants {
              role
              name
              organisationMembershipId
            }
            purpose
            origin
            inFlightCandidateCount
          }
          archivedSteps {
            continuityId
            lastLabel
            lastStepType
            removedAt
            removedBy
            changeReason
            inFlightCandidateCount
          }
        }
      }
      errors
    }
  }
`;

export const GENERATE_STEP_ASSESSMENT_PLAN = gql`
  mutation GenerateStepAssessmentPlan($input: GenerateStepAssessmentPlanInput!) {
    generateStepAssessmentPlan(input: $input) {
      enqueued
      errors
      status
      warning
      deepLinkPath
      deepLinkLabel
    }
  }
`;

export const SUGGEST_STEP_ASSESSMENT_PLAN_COVERAGE = gql`
  mutation SuggestStepAssessmentPlanCoverage($input: SuggestStepAssessmentPlanCoverageInput!) {
    suggestStepAssessmentPlanCoverage(input: $input) {
      enqueued
      errors
      status
      warning
      deepLinkPath
      deepLinkLabel
    }
  }
`;

export const SUGGEST_SELECTION_PROCESS_PLANS = gql`
  mutation SuggestSelectionProcessPlans($input: SuggestSelectionProcessPlansInput!) {
    suggestSelectionProcessPlans(input: $input) {
      enqueued
      errors
      candidateCount
    }
  }
`;

export const ACKNOWLEDGE_STEP_ASSESSMENT_PLAN = gql`
  mutation AcknowledgeStepAssessmentPlan($input: AcknowledgeStepAssessmentPlanInput!) {
    acknowledgeStepAssessmentPlan(input: $input) {
      stepAssessmentPlan {
        id
        currentVersion {
          id
          versionNumber
          origin
          acknowledgedAt
        }
      }
      errors
    }
  }
`;

export const CREATE_INVITATION = gql`
  mutation CreateInvitation($input: CreateInvitationInput!) {
    createInvitation(input: $input) {
      invitation {
        id
        email
        role
        expiresAt
        invitedBy {
          id
          fullName
        }
        createdAt
      }
      errors
    }
  }
`;

export const RESEND_INVITATION = gql`
  mutation ResendInvitation($input: ResendInvitationInput!) {
    resendInvitation(input: $input) {
      invitation {
        id
        expiresAt
      }
      errors
    }
  }
`;

export const CANCEL_INVITATION = gql`
  mutation CancelInvitation($input: CancelInvitationInput!) {
    cancelInvitation(input: $input) {
      success
      errors
    }
  }
`;

export const UPDATE_MEMBER_ROLE = gql`
  mutation UpdateMemberRole($input: UpdateMemberRoleInput!) {
    updateMemberRole(input: $input) {
      membership {
        id
        role
      }
      errors
    }
  }
`;

export const REMOVE_MEMBER = gql`
  mutation RemoveMember($input: RemoveMemberInput!) {
    removeMember(input: $input) {
      success
      errors
    }
  }
`;

export const UPDATE_ROLE_BLUEPRINT = gql`
  mutation UpdateRoleBlueprint($input: UpdateRoleBlueprintInput!) {
    updateRoleBlueprint(input: $input) {
      roleBlueprint {
        id
        capabilityDimensions {
          id
          name
          description
          position
          idealScore
          positiveIndicators
          negativeIndicators
        }
        currentVersion {
          id
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      errors
    }
  }
`;

export const CASCADE_GENERATE_BLUEPRINT_ARTEFACTS = gql`
  mutation CascadeGenerateBlueprintArtefacts(
    $input: CascadeGenerateBlueprintArtefactsInput!
  ) {
    cascadeGenerateBlueprintArtefacts(input: $input) {
      enqueued
      errors
      status
      warning
      manualEditsKinds
    }
  }
`;

export const START_REGENERATE_RUN = gql`
  mutation StartRegenerateRun($input: StartRegenerateRunInput!) {
    startRegenerateRun(input: $input) {
      status
      parentTaskId
      enqueuedKinds
      skippedManualEdits
      errors
    }
  }
`;

export const REGENERATE_ROLE_BLUEPRINT_DIMENSIONS = gql`
  mutation RegenerateRoleBlueprintDimensions(
    $input: RegenerateRoleBlueprintDimensionsInput!
  ) {
    regenerateRoleBlueprintDimensions(input: $input) {
      enqueued
      errors
      status
      warning
      reason
    }
  }
`;

export const UPDATE_ASSESSMENT_SCORES = gql`
  mutation UpdateAssessmentScores($input: UpdateAssessmentScoresInput!) {
    updateAssessmentScores(input: $input) {
      status
      reason
      enqueued
      errors
    }
  }
`;

export const TOGGLE_ROLE_BLUEPRINT_LOCK = gql`
  mutation ToggleRoleBlueprintLock($input: ToggleRoleBlueprintLockInput!) {
    toggleRoleBlueprintLock(input: $input) {
      roleBlueprint {
        id
        locked
        lockedAt
        lockedBy {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const RELOCK_ROLE_BLUEPRINT = gql`
  mutation RelockRoleBlueprint($input: RelockRoleBlueprintInput!) {
    relockRoleBlueprint(input: $input) {
      roleBlueprint {
        id
        locked
        hasUncommittedEdits
        currentVersion {
          id
          versionNumber
          changeSummary
        }
      }
      errors
    }
  }
`;

export const TOGGLE_ROLE_BLUEPRINT_APPROVAL = gql`
  mutation ToggleRoleBlueprintApproval($input: ToggleRoleBlueprintApprovalInput!) {
    toggleRoleBlueprintApproval(input: $input) {
      roleBlueprint {
        id
        clientApproved
        clientApprovedAt
        clientApprovedBy
      }
      errors
    }
  }
`;

export const UPDATE_CAPABILITY_DIMENSIONS = gql`
  mutation UpdateCapabilityDimensions($input: UpdateCapabilityDimensionsInput!) {
    updateCapabilityDimensions(input: $input) {
      roleBlueprint {
        id
        capabilityDimensions {
          id
          continuityId
          name
          description
          position
          idealScore
          positiveIndicators
          negativeIndicators
          critical
        }
      }
      errors
    }
  }
`;

export const UPSERT_HIRE_THRESHOLD_RULE = gql`
  mutation UpsertHireThresholdRule($input: UpsertHireThresholdRuleInput!) {
    upsertHireThresholdRule(input: $input) {
      hireThresholdRule {
        id
        minScoreForCriticalDimensions
        minOverallScore
        minStrengthsCount
        default
      }
      errors
    }
  }
`;

export const OVERRIDE_ASSESSMENT_THRESHOLD = gql`
  mutation OverrideAssessmentThreshold($input: OverrideAssessmentThresholdInput!) {
    overrideAssessmentThreshold(input: $input) {
      candidateAssessmentVersion {
        id
        thresholdPassing
        thresholdPassingWithOverride
        thresholdOverriddenAt
        thresholdOverrideReason
        thresholdOverriddenBy { id fullName }
      }
      errors
    }
  }
`;

export const CLEAR_ASSESSMENT_THRESHOLD_OVERRIDE = gql`
  mutation ClearAssessmentThresholdOverride($input: ClearAssessmentThresholdOverrideInput!) {
    clearAssessmentThresholdOverride(input: $input) {
      candidateAssessmentVersion {
        id
        thresholdPassing
        thresholdPassingWithOverride
        thresholdOverriddenAt
        thresholdOverrideReason
        thresholdOverriddenBy { id fullName }
      }
      errors
    }
  }
`;

export const UPDATE_PERSONAS = gql`
  mutation UpdatePersonas($input: UpdatePersonasInput!) {
    updatePersonas(input: $input) {
      campaignBrief {
        id
        personas {
          id
          name
          summary
          description
          position
        }
      }
      errors
    }
  }
`;

export const GENERATE_PERSONAS = gql`
  mutation GeneratePersonas($input: GeneratePersonasInput!) {
    generatePersonas(input: $input) {
      enqueued
      errors
    }
  }
`;

export const GENERATE_ROLE_BLUEPRINT = gql`
  mutation GenerateRoleBlueprint($input: GenerateRoleBlueprintInput!) {
    generateRoleBlueprint(input: $input) {
      enqueued
      errors
    }
  }
`;

export const GENERATE_MARKET_MAP = gql`
  mutation GenerateMarketMap($input: GenerateMarketMapInput!) {
    generateMarketMap(input: $input) {
      enqueued
      errors
    }
  }
`;

export const GENERATE_BOOLEAN_STRINGS = gql`
  mutation GenerateBooleanStrings($input: GenerateBooleanStringsInput!) {
    generateBooleanStrings(input: $input) {
      enqueued
      errors
    }
  }
`;

export const UPDATE_SCREENING_CRITERIA = gql`
  mutation UpdateScreeningCriteria($input: UpdateScreeningCriteriaInput!) {
    updateScreeningCriteria(input: $input) {
      screeningCriteria {
        id
        locked
        state
        currentVersion {
          id
          versionNumber
          effectiveFrom
          changeSummary
          mustHaveCriteria
          niceToHaveCriteria
          exclusions
        }
      }
      errors
    }
  }
`;

export const TOGGLE_SCREENING_CRITERIA_LOCK = gql`
  mutation ToggleScreeningCriteriaLock($input: ToggleScreeningCriteriaLockInput!) {
    toggleScreeningCriteriaLock(input: $input) {
      screeningCriteria {
        id
        locked
        lockedAt
        lockedBy {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const SCORE_CANDIDATE_SCORING_MATRIX_CELL = gql`
  mutation ScoreCandidateScoringMatrixCell($input: ScoreCandidateScoringMatrixCellInput!) {
    scoreCandidateScoringMatrixCell(input: $input) {
      cell {
        capabilityDimensionId
        competencyScoreId
        score
        evidence
        anchorLabel
        idealScore
        flagsThreshold
        stale
        staleReasons
        currentBandBody
      }
      errors
    }
  }
`;

export const TOGGLE_CANDIDATE_SCORING_MATRIX_LOCK = gql`
  mutation ToggleCandidateScoringMatrixLock($input: ToggleCandidateScoringMatrixLockInput!) {
    toggleCandidateScoringMatrixLock(input: $input) {
      candidateScoringMatrix {
        id
        locked
        lockedAt
        state
        lockedBy {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const GENERATE_ASSESSMENT_MATRIX = gql`
  mutation GenerateAssessmentMatrix($input: GenerateAssessmentMatrixInput!) {
    generateAssessmentMatrix(input: $input) {
      enqueued
      errors
      status
      warning
      deepLinkPath
      deepLinkLabel
    }
  }
`;

export const REGENERATE_CAMPAIGN_DECISION_SUMMARY = gql`
  mutation RegenerateCampaignDecisionSummary($input: RegenerateCampaignDecisionSummaryInput!) {
    regenerateCampaignDecisionSummary(input: $input) {
      campaign {
        id
      }
      errors
    }
  }
`;

export const UPDATE_ASSESSMENT_MATRIX_BAND = gql`
  mutation UpdateAssessmentMatrixBand($input: UpdateAssessmentMatrixBandInput!) {
    updateAssessmentMatrixBand(input: $input) {
      band {
        id
        capabilityDimensionId
        level
        levelLabel
        body
        position
        effectiveFrom
      }
      errors
    }
  }
`;

export const TOGGLE_ASSESSMENT_MATRIX_LOCK = gql`
  mutation ToggleAssessmentMatrixLock($input: ToggleAssessmentMatrixLockInput!) {
    toggleAssessmentMatrixLock(input: $input) {
      assessmentMatrix {
        id
        locked
        lockedAt
        state
        lockedBy {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const CREATE_TARGET_SECTOR = gql`
  mutation CreateTargetSector($input: CreateTargetSectorInput!) {
    createTargetSector(input: $input) {
      targetSector {
        id
        name
        relevance
        position
        notes
      }
      errors
    }
  }
`;

export const UPDATE_TARGET_SECTOR = gql`
  mutation UpdateTargetSector($input: UpdateTargetSectorInput!) {
    updateTargetSector(input: $input) {
      targetSector {
        id
        name
        relevance
        notes
        excluded
      }
      errors
    }
  }
`;

export const DELETE_TARGET_SECTOR = gql`
  mutation DeleteTargetSector($input: DeleteTargetSectorInput!) {
    deleteTargetSector(input: $input) {
      success
      errors
    }
  }
`;

export const CREATE_TARGET_COMPANY = gql`
  mutation CreateTargetCompany($input: CreateTargetCompanyInput!) {
    createTargetCompany(input: $input) {
      targetCompany {
        id
        name
        relevance
        employeeCountRange
        website
        notes
        targetSector {
          id
          name
        }
      }
      errors
    }
  }
`;

export const UPDATE_TARGET_COMPANY = gql`
  mutation UpdateTargetCompany($input: UpdateTargetCompanyInput!) {
    updateTargetCompany(input: $input) {
      targetCompany {
        id
        name
        relevance
        employeeCountRange
        website
        notes
        excluded
        targetSector {
          id
          name
        }
      }
      errors
    }
  }
`;

export const DELETE_TARGET_COMPANY = gql`
  mutation DeleteTargetCompany($input: DeleteTargetCompanyInput!) {
    deleteTargetCompany(input: $input) {
      success
      errors
    }
  }
`;

export const UPDATE_MARKET_MAP_NOTES = gql`
  mutation UpdateMarketMapNotes($input: UpdateMarketMapNotesInput!) {
    updateMarketMapNotes(input: $input) {
      marketMap {
        id
        notes
      }
      errors
    }
  }
`;

export const ASSESS_CANDIDATE = gql`
  mutation AssessCandidate($input: AssessCandidateInput!) {
    assessCandidate(input: $input) {
      status
      campaignCandidateId
      candidateName
      message
      errors
    }
  }
`;

export const REGENERATE_INTERVIEW_ROUND_SYNTHESIS = gql`
  mutation RegenerateInterviewRoundSynthesis($input: RegenerateInterviewRoundSynthesisInput!) {
    regenerateInterviewRoundSynthesis(input: $input) {
      enqueued
      errors
      status
      warning
      deepLinkPath
      deepLinkLabel
    }
  }
`;

export const SAVE_CANDIDATE_ASSESSMENT = gql`
  mutation SaveCandidateAssessment($input: SaveCandidateAssessmentInput!) {
    saveCandidateAssessment(input: $input) {
      candidateAssessment {
        id
        currentVersion {
          id
          overallScore
          recommendation
          overallSummary
          strengths
          concerns
          versionNumber
          assessor {
            id
            fullName
          }
          thresholdPassing
          thresholdPassingWithOverride
          thresholdOverriddenAt
          thresholdOverrideReason
          thresholdOverriddenBy { id fullName }
          thresholdFailures { type dimensionId dimensionName score overall count min }
          competencyScores {
            id
            score
            anchorLabel
            evidence
            notes
            detailedNotes
            capabilityDimension {
              id
              name
            }
            behaviouralAnchors {
              id
              evidenceText
              sourceExcerpt
              sourceArtefactType
              sourceArtefactId
              aiGenerated
              createdAt
            }
          }
        }
      }
      errors
    }
  }
`;

export const SUGGEST_COMPETENCY_SCORE_EVIDENCE = gql`
  mutation SuggestCompetencyScoreEvidence($input: SuggestCompetencyScoreEvidenceInput!) {
    suggestCompetencyScoreEvidence(input: $input) {
      enqueued
      errors
    }
  }
`;

export const CREATE_OUTREACH_ATTEMPT = gql`
  mutation CreateOutreachAttempt($input: CreateOutreachAttemptInput!) {
    createOutreachAttempt(input: $input) {
      outreachAttempt {
        id
        channel
        direction
        status
        messageContent
        sentAt
        sentBy {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const CREATE_OFFER = gql`
  mutation CreateOffer($input: CreateOfferInput!) {
    createOffer(input: $input) {
      offer {
        id
        status
        baseSalary
        currency
        bonusDetails
        equityDetails
        startDate
        notes
      }
      errors
    }
  }
`;

export const UPDATE_OFFER = gql`
  mutation UpdateOffer($input: UpdateOfferInput!) {
    updateOffer(input: $input) {
      offer {
        id
        status
        baseSalary
        currency
        bonusDetails
        equityDetails
        startDate
        notes
      }
      errors
    }
  }
`;

export const UPDATE_CAMPAIGN_CANDIDATE = gql`
  mutation UpdateCampaignCandidate($input: UpdateCampaignCandidateInput!) {
    updateCampaignCandidate(input: $input) {
      campaignCandidate {
        id
        notes
      }
      errors
    }
  }
`;

export const UPDATE_OUTREACH_ATTEMPT = gql`
  mutation UpdateOutreachAttempt($input: UpdateOutreachAttemptInput!) {
    updateOutreachAttempt(input: $input) {
      outreachAttempt {
        id
        status
        responseContent
        respondedAt
      }
      errors
    }
  }
`;

export const COPY_TO_CAMPAIGN = gql`
  mutation CopyToCampaign($input: CopyToCampaignInput!) {
    copyToCampaign(input: $input) {
      campaignCandidate {
        id
        stage
        candidate {
          id
          fullName
        }
      }
      errors
    }
  }
`;

export const EXPORT_LONGLIST_EXECUTIVE_SUMMARY_TO_GOOGLE_DOCS = gql`
  mutation ExportLonglistExecutiveSummaryToGoogleDocs(
    $input: ExportLonglistExecutiveSummaryToGoogleDocsInput!
  ) {
    exportLonglistExecutiveSummaryToGoogleDocs(input: $input) {
      documentExport {
        id
        status
        kind
      }
      errors
    }
  }
`;

export const EXPORT_SELECTION_PROCESS_TO_GOOGLE_DOCS = gql`
  mutation ExportSelectionProcessToGoogleDocs(
    $input: ExportSelectionProcessToGoogleDocsInput!
  ) {
    exportSelectionProcessToGoogleDocs(input: $input) {
      documentExport {
        id
        status
        kind
      }
      errors
    }
  }
`;

export const SAVE_SEARCH_QUERY = gql`
  mutation SaveSearchQuery($input: SaveSearchQueryInput!) {
    saveSearchQuery(input: $input) {
      searchQuery
      errors
    }
  }
`;

export const CREATE_INTERVIEW_CONTRIBUTION = gql`
  mutation CreateInterviewContribution($input: CreateInterviewContributionInput!) {
    createInterviewContribution(input: $input) {
      interviewContribution {
        id
        kind
        candidateSelectionStepId
        occurredAt
        createdAt
        contributor {
          id
          fullName
        }
        note {
          interviewType
          rawNotes
          durationMinutes
          recordingNotes
        }
        transcript {
          content
          source
          originalFilename
          extractionState
        }
        assessmentFormSubmission {
          recommendation
          summary
          extractionState
          extractionError
          residualNotes
          importedFileUrl
          importedFileFilename
          reExtractable
        }
      }
      errors
    }
  }
`;

export const UPDATE_INTERVIEW_CONTRIBUTION = gql`
  mutation UpdateInterviewContribution($input: UpdateInterviewContributionInput!) {
    updateInterviewContribution(input: $input) {
      interviewContribution {
        id
        kind
        occurredAt
        updatedAt
        note {
          interviewType
          rawNotes
          durationMinutes
          recordingNotes
        }
        transcript {
          content
          source
        }
      }
      errors
    }
  }
`;

export const DELETE_INTERVIEW_CONTRIBUTION = gql`
  mutation DeleteInterviewContribution($input: DeleteInterviewContributionInput!) {
    deleteInterviewContribution(input: $input) {
      id
      errors
    }
  }
`;

export const IMPORT_ASSESSMENT_FORM_RESPONSE = gql`
  mutation ImportAssessmentFormResponse($input: ImportAssessmentFormResponseInput!) {
    importAssessmentFormResponse(input: $input) {
      interviewContribution {
        id
        kind
        candidateSelectionStepId
        assessmentFormSubmission {
          extractionState
          extractionError
          residualNotes
          importedFileUrl
          importedFileFilename
          reExtractable
        }
      }
      errors
    }
  }
`;

export const RE_EXTRACT_ASSESSMENT_FORM_RESPONSE = gql`
  mutation ReExtractAssessmentFormResponse($input: ReExtractAssessmentFormResponseInput!) {
    reExtractAssessmentFormResponse(input: $input) {
      interviewContribution {
        id
        assessmentFormSubmission {
          extractionState
          extractionError
          residualNotes
          reExtractable
        }
      }
      errors
    }
  }
`;

// --- CandidateSelectionStep journey (Epic B #2230) ---

export const ADVANCE_CANDIDATE_SELECTION_STEP = gql`
  mutation AdvanceCandidateSelectionStep($input: AdvanceCandidateSelectionStepInput!) {
    advanceCandidateSelectionStep(input: $input) {
      candidateSelectionSteps {
        id
        round
        status
        outcome
        rating
        stepType
        label
        createdAt
      }
      currentStep {
        id
        round
      }
      errors
    }
  }
`;

export const REOPEN_CANDIDATE_SELECTION_STEP = gql`
  mutation ReopenCandidateSelectionStep($input: ReopenCandidateSelectionStepInput!) {
    reopenCandidateSelectionStep(input: $input) {
      candidateSelectionSteps {
        id
        round
        status
        outcome
        rating
        stepType
        label
        continuityId
        createdAt
      }
      currentStep {
        id
        round
      }
      status
      errors
    }
  }
`;

export const AMEND_CANDIDATE_SELECTION_STEP = gql`
  mutation AmendCandidateSelectionStep($input: AmendCandidateSelectionStepInput!) {
    amendCandidateSelectionStep(input: $input) {
      candidateSelectionStep {
        id
        outcome
        rating
        amended
        outcomeContradictsLaterActivity
      }
      amendment {
        id
        fromOutcome
        toOutcome
        fromRating
        toRating
        reason
      }
      status
      errors
    }
  }
`;

export const MATERIALISE_CANDIDATE_SELECTION_STEPS = gql`
  mutation MaterialiseCandidateSelectionSteps($input: MaterialiseCandidateSelectionStepsInput!) {
    materialiseCandidateSelectionSteps(input: $input) {
      candidateSelectionSteps {
        id
        round
        status
        outcome
        rating
        stepType
        label
        createdAt
      }
      errors
    }
  }
`;

export const UPDATE_CANDIDATE_SELECTION_STEP = gql`
  mutation UpdateCandidateSelectionStep($input: UpdateCandidateSelectionStepInput!) {
    updateCandidateSelectionStep(input: $input) {
      candidateSelectionStep {
        id
        round
        status
        outcome
        rating
        stepType
        label
        candidatePrepNotes
        createdAt
      }
      errors
    }
  }
`;

export const CREATE_NOTE = gql`
  mutation CreateNote($input: CreateNoteInput!) {
    createNote(input: $input) {
      note {
        id
        content
        noteType
        visibility
        author {
          id
          fullName
        }
        createdAt
      }
      errors
    }
  }
`;

export const UPDATE_NOTE = gql`
  mutation UpdateNote($input: UpdateNoteInput!) {
    updateNote(input: $input) {
      note {
        id
        content
        noteType
        visibility
        updatedAt
      }
      errors
    }
  }
`;

export const UPDATE_CAMPAIGN_SUGGESTION = gql`
  mutation UpdateCampaignSuggestion($input: UpdateCampaignSuggestionInput!) {
    updateCampaignSuggestion(input: $input) {
      campaignSuggestion {
        id
        status
      }
      errors
    }
  }
`;

const POOL_VIEW_FIELDS = `
  id
  name
  sortBy
  sortDirection
  search
  stages
  consentStatuses
  activeSince
`;

export const CREATE_CANDIDATE_POOL_VIEW = gql`
  mutation CreateCandidatePoolView($input: CreateCandidatePoolViewInput!) {
    createCandidatePoolView(input: $input) {
      candidatePoolView {
        ${POOL_VIEW_FIELDS}
      }
      errors
    }
  }
`;

export const UPDATE_CANDIDATE_POOL_VIEW = gql`
  mutation UpdateCandidatePoolView($input: UpdateCandidatePoolViewInput!) {
    updateCandidatePoolView(input: $input) {
      candidatePoolView {
        ${POOL_VIEW_FIELDS}
      }
      errors
    }
  }
`;

export const DELETE_CANDIDATE_POOL_VIEW = gql`
  mutation DeleteCandidatePoolView($input: DeleteCandidatePoolViewInput!) {
    deleteCandidatePoolView(input: $input) {
      deletedId
      errors
    }
  }
`;

export const CREATE_TALENT_POOL = gql`
  mutation CreateTalentPool($input: CreateTalentPoolInput!) {
    createTalentPool(input: $input) {
      talentPool {
        id
        name
        description
        candidateCount
        campaign {
          id
          title
        }
      }
      errors
    }
  }
`;

export const ADD_TO_TALENT_POOL = gql`
  mutation AddToTalentPool($input: AddToTalentPoolInput!) {
    addToTalentPool(input: $input) {
      talentPoolMembership {
        id
        candidate {
          id
          fullName
        }
        relevanceNotes
      }
      errors
    }
  }
`;

export const MOVE_CANDIDATE_STAGE = gql`
  mutation MoveCandidateStage($input: MoveCandidateStageInput!) {
    moveCandidateStage(input: $input) {
      campaignCandidate {
        id
        stage
      }
      errors
    }
  }
`;

export const CREATE_DOCUMENT_TEMPLATE = gql`
  mutation CreateDocumentTemplate($input: CreateDocumentTemplateInput!) {
    createDocumentTemplate(input: $input) {
      documentTemplate {
        id
        name
        templateType
        content
        branding
        isDefault
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

export const UPDATE_DOCUMENT_TEMPLATE = gql`
  mutation UpdateDocumentTemplate($input: UpdateDocumentTemplateInput!) {
    updateDocumentTemplate(input: $input) {
      documentTemplate {
        id
        name
        templateType
        content
        branding
        isDefault
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

export const EXPORT_DOCUMENT_TEMPLATE_TO_GOOGLE_DOCS = gql`
  mutation ExportDocumentTemplateToGoogleDocs($input: ExportDocumentTemplateToGoogleDocsInput!) {
    exportDocumentTemplateToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_CANDIDATE_REPORT_TO_GOOGLE_DOCS = gql`
  mutation ExportCandidateReportToGoogleDocs($input: ExportCandidateReportToGoogleDocsInput!) {
    exportCandidateReportToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_CANDIDATE_ASSESSMENT_TO_GOOGLE_DOCS = gql`
  mutation ExportCandidateAssessmentToGoogleDocs($input: ExportCandidateAssessmentToGoogleDocsInput!) {
    exportCandidateAssessmentToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const IMPORT_DEMO_DATA = gql`
  mutation ImportDemoData($input: ImportDemoDataInput!) {
    importDemoData(input: $input) {
      campaign {
        id
        title
      }
      errors
    }
  }
`;

export const UPDATE_ORGANISATION_AI_SETTINGS = gql`
  mutation UpdateOrganisationAiSettings($input: UpdateOrganisationAiSettingsInput!) {
    updateOrganisationAiSettings(input: $input) {
      organisation {
        id
        aiPositiveInstructions
        aiNegativeInstructions
        llmModel
        aiAvailableModels {
          label
          models {
            id
            label
          }
        }
      }
      errors
    }
  }
`;

export const UPDATE_DATA_RETENTION_POLICIES = gql`
  mutation UpdateDataRetentionPolicies($input: UpdateDataRetentionPoliciesInput!) {
    updateDataRetentionPolicies(input: $input) {
      dataRetentionPolicies {
        id
        resourceType
        retentionDays
        enabled
      }
      errors
    }
  }
`;

export const UPDATE_ORGANISATION_PII_SETTINGS = gql`
  mutation UpdateOrganisationPiiSettings($input: UpdateOrganisationPiiSettingsInput!) {
    updateOrganisationPiiSettings(input: $input) {
      organisation {
        id
        piiMaskingEnabled
      }
      errors
    }
  }
`;

export const UPDATE_SUPERUSER_NOTIFICATION_SETTING = gql`
  mutation UpdateSuperuserNotificationSetting($input: UpdateSuperuserNotificationSettingInput!) {
    updateSuperuserNotificationSetting(input: $input) {
      organisation {
        id
        notifyAdminsOnSuperuserAction
      }
      errors
    }
  }
`;

export const UPDATE_ORGANISATION_BRANDING = gql`
  mutation UpdateOrganisationBranding($input: UpdateOrganisationBrandingInput!) {
    updateOrganisationBranding(input: $input) {
      organisation {
        id
        branding
        logoUrl
      }
      errors
    }
  }
`;

export const UPDATE_ORGANISATION_SEARCH_LOCATION = gql`
  mutation UpdateOrganisationSearchLocation($input: UpdateOrganisationSearchLocationInput!) {
    updateOrganisationSearchLocation(input: $input) {
      organisation {
        id
        searchCountryCodes
        searchLocations {
          name
          countryCode
        }
      }
      errors
    }
  }
`;

export const ACCEPT_TERMS = gql`
  mutation AcceptTerms($input: AcceptTermsInput!) {
    acceptTerms(input: $input) {
      success
      errors
    }
  }
`;

export const EXPORT_SHORTLIST_REPORT_TO_GOOGLE_DOCS = gql`
  mutation ExportShortlistReportToGoogleDocs($input: ExportShortlistReportToGoogleDocsInput!) {
    exportShortlistReportToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_ASSESSMENT_MATRIX_TO_GOOGLE_DOCS = gql`
  mutation ExportAssessmentMatrixToGoogleDocs($input: ExportAssessmentMatrixToGoogleDocsInput!) {
    exportAssessmentMatrixToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_ASSESSMENT_MATRIX_TO_GOOGLE_SHEETS = gql`
  mutation ExportAssessmentMatrixToGoogleSheets($input: ExportAssessmentMatrixToGoogleSheetsInput!) {
    exportAssessmentMatrixToGoogleSheets(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_CANDIDATE_SCORING_MATRIX_TO_GOOGLE_DOCS = gql`
  mutation ExportCandidateScoringMatrixToGoogleDocs($input: ExportCandidateScoringMatrixToGoogleDocsInput!) {
    exportCandidateScoringMatrixToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_CANDIDATE_SCORING_MATRIX_TO_GOOGLE_SHEETS = gql`
  mutation ExportCandidateScoringMatrixToGoogleSheets($input: ExportCandidateScoringMatrixToGoogleSheetsInput!) {
    exportCandidateScoringMatrixToGoogleSheets(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const UPDATE_INTERVIEW_GUIDE = gql`
  mutation UpdateInterviewGuide($input: UpdateInterviewGuideInput!) {
    updateInterviewGuide(input: $input) {
      interviewGuide {
        id
        selectionStepId
        stepType
        totalVersions
        currentVersion {
          id
          content
          versionNumber
          changeSummary
          effectiveFrom
          author {
            id
            fullName
          }
        }
      }
      errors
    }
  }
`;

export const UPDATE_ASSESSMENT_GUIDE = gql`
  mutation UpdateAssessmentGuide($input: UpdateAssessmentGuideInput!) {
    updateAssessmentGuide(input: $input) {
      assessmentGuide {
        id
        totalVersions
        currentVersion {
          id
          content
          versionNumber
          changeSummary
          effectiveFrom
          author {
            id
            fullName
          }
        }
      }
      errors
    }
  }
`;

export const GENERATE_ASSESSMENT_GUIDE = gql`
  mutation GenerateAssessmentGuide($input: GenerateAssessmentGuideInput!) {
    generateAssessmentGuide(input: $input) {
      enqueued
      errors
      status
      warning
      deepLinkPath
      deepLinkLabel
    }
  }
`;

export const EXPORT_INTERVIEW_GUIDE_TO_GOOGLE_DOCS = gql`
  mutation ExportInterviewGuideToGoogleDocs($input: ExportInterviewGuideToGoogleDocsInput!) {
    exportInterviewGuideToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const GENERATE_INTERVIEW_PACK = gql`
  mutation GenerateInterviewPack($input: GenerateInterviewPackInput!) {
    generateInterviewPack(input: $input) {
      enqueued
      errors
    }
  }
`;

export const EXPORT_INTERVIEW_PACK_TO_GOOGLE_DOCS = gql`
  mutation ExportInterviewPackToGoogleDocs($input: ExportInterviewPackToGoogleDocsInput!) {
    exportInterviewPackToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_ASSESSMENT_FORM_TO_GOOGLE_DOCS = gql`
  mutation ExportAssessmentFormToGoogleDocs($input: ExportAssessmentFormToGoogleDocsInput!) {
    exportAssessmentFormToGoogleDocs(input: $input) {
      documentExport { id status kind googleDocUrl errorMessage }
      errors
    }
  }
`;

export const EXPORT_CANDIDATE_DATA = gql`
  mutation ExportCandidateData($input: ExportCandidateDataInput!) {
    exportCandidateData(input: $input) {
      downloadUrl
      errors
    }
  }
`;

export const ANONYMIZE_CANDIDATE = gql`
  mutation AnonymizeCandidate($input: AnonymizeCandidateInput!) {
    anonymizeCandidate(input: $input) {
      candidate {
        id
        firstName
        lastName
        email
        phone
        consentStatus
      }
      errors
    }
  }
`;

export const DELETE_CANDIDATE = gql`
  mutation DeleteCandidate($input: DeleteCandidateInput!) {
    deleteCandidate(input: $input) {
      deletedId
      errors
    }
  }
`;

export const GENERATE_LINKEDIN_JOB_POSTING = gql`
  mutation GenerateLinkedinJobPosting($input: GenerateLinkedinJobPostingInput!) {
    generateLinkedinJobPosting(input: $input) {
      linkedinJobPosting {
        id
        kind
        totalVersions
        currentVersion {
          id
          body
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      errors
    }
  }
`;

export const GENERATE_BRANDED_JOB_POSTING = gql`
  mutation GenerateBrandedJobPosting($input: GenerateBrandedJobPostingInput!) {
    generateBrandedJobPosting(input: $input) {
      brandedJobPosting {
        id
        kind
        totalVersions
        currentVersion {
          id
          body
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      errors
    }
  }
`;

export const UPDATE_CAMPAIGN_JOB_POSTING = gql`
  mutation UpdateCampaignJobPosting($input: UpdateCampaignJobPostingInput!) {
    updateCampaignJobPosting(input: $input) {
      linkedinJobPosting {
        id
        kind
        totalVersions
        currentVersion {
          id
          body
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      errors
    }
  }
`;

export const GENERATE_CANDIDATE_LINKEDIN_MESSAGES = gql`
  mutation GenerateCandidateLinkedinMessages($input: GenerateCandidateLinkedinMessagesInput!) {
    generateCandidateLinkedinMessages(input: $input) {
      candidateOutreachMessages {
        id
        kind
        totalVersions
        currentVersion {
          id
          messages { label body }
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      errors
    }
  }
`;

export const GENERATE_CANDIDATE_LINKEDIN_CONNECTION_REQUESTS = gql`
  mutation GenerateCandidateLinkedinConnectionRequests(
    $input: GenerateCandidateLinkedinConnectionRequestsInput!
  ) {
    generateCandidateLinkedinConnectionRequests(input: $input) {
      candidateOutreachMessages {
        id
        kind
        totalVersions
        currentVersion {
          id
          messages { label body }
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      errors
    }
  }
`;

export const GENERATE_CANDIDATE_EMAIL_MESSAGES = gql`
  mutation GenerateCandidateEmailMessages($input: GenerateCandidateEmailMessagesInput!) {
    generateCandidateEmailMessages(input: $input) {
      candidateOutreachMessages {
        id
        kind
        totalVersions
        currentVersion {
          id
          messages { label body }
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      status
      warning
      blockedCampaignCandidateIds
      errors
    }
  }
`;

export const UPDATE_CANDIDATE_OUTREACH_MESSAGE = gql`
  mutation UpdateCandidateOutreachMessage($input: UpdateCandidateOutreachMessageInput!) {
    updateCandidateOutreachMessage(input: $input) {
      candidateOutreachMessage {
        id
        kind
        totalVersions
        currentVersion {
          id
          messages { label body }
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
      errors
    }
  }
`;

export const FETCH_DRIVE_FILE = gql`
  mutation FetchDriveFile($input: FetchDriveFileInput!) {
    fetchDriveFile(input: $input) {
      backgroundTask {
        id
        kind
        title
        state
      }
      errors
    }
  }
`;

export const RUN_XRAY_SEARCH = gql`
  mutation RunXraySearch($input: RunXraySearchInput!) {
    runXraySearch(input: $input) {
      xraySearchRun {
        id
        searchQueryId
        queryString
        status
        resultCount
      }
      errors
    }
  }
`;

export const RUN_ALL_XRAY_SEARCHES = gql`
  mutation RunAllXraySearches($input: RunAllXraySearchesInput!) {
    runAllXraySearches(input: $input) {
      xraySearchRuns {
        id
        searchQueryId
        queryString
        status
        resultCount
      }
      errors
    }
  }
`;

export const CLEAR_XRAY_SEARCH_RESULTS = gql`
  mutation ClearXraySearchResults($input: ClearXraySearchResultsInput!) {
    clearXraySearchResults(input: $input) {
      cleared
      errors
    }
  }
`;

export const CLEAR_XRAY_SEARCH_RUN = gql`
  mutation ClearXraySearchRun($input: ClearXraySearchRunInput!) {
    clearXraySearchRun(input: $input) {
      cleared
      errors
    }
  }
`;

export const DISMISS_DISCOVERED_PROFILE = gql`
  mutation DismissDiscoveredProfile($input: DismissDiscoveredProfileInput!) {
    dismissDiscoveredProfile(input: $input) {
      success
      errors
    }
  }
`;

export const UPDATE_DISCOVERED_PROFILE_LOCATION = gql`
  mutation UpdateDiscoveredProfileLocation($input: UpdateDiscoveredProfileLocationInput!) {
    updateDiscoveredProfileLocation(input: $input) {
      success
      errors
    }
  }
`;

export const UPDATE_ORGANISATION_RESIDENCY_REQUIREMENT = gql`
  mutation UpdateOrganisationResidencyRequirement($input: UpdateOrganisationResidencyRequirementInput!) {
    updateOrganisationResidencyRequirement(input: $input) {
      organisation {
        id
        residencyRequirement
        llmModel
      }
      errors
    }
  }
`;

export const DISMISS_CHANGELOG = gql`
  mutation DismissChangelog($input: DismissChangelogInput!) {
    dismissChangelog(input: $input) {
      success
      errors
    }
  }
`;
